import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import ProjectTypeForm from "@/modules/projectTypes/components/ProjectTypeForm";
import { useProjectTypesDictionary } from "@/modules/projectTypes/hooks/useProjectTypesDictionary";
import { ApiError } from "@/services/api";
import { ReadProjectType, CreateProjectType } from "@/types/projectType";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function ProjectTypeCreator() {
  const id = "new";
  const { t } = useTranslation();
  const config = useProjectTypesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.ProjectTypes);
  const labels = config.getLabels(t);

  const mutationFn: MutationFunction<
    ReadProjectType,
    CreateProjectType
  > = async (values) => {
    const response = await config.service.post(values);
    return response;
  };

  const mutation = useMutation<ReadProjectType, ApiError, CreateProjectType>({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<CreateProjectType>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<CreateProjectType>,
    disabled: mutation.isPending,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <ProjectTypeForm includeProjectDependsOn={true} />
    </DictionaryRecordContextProvider>
  );
}
